'use client';

import { useEffect, useRef } from 'react';

import {
  dismissBatchJob,
  useBatchReplyV3Status,
  useConfirmBatchReplyV3,
} from '@/api-hooks/useBatchReplyV3';
import { isFailedTerminalV3Status, isSuccessTerminalV3Status } from '@/apis/batch-replies-v3.api';
import { useMobile } from '@/hooks/useMobile';

import {
  useBatchReplyV3Expanded,
  useBatchReplyV3JobId,
  useBatchReplyV3JobIdValue,
  useOptimisticDim,
  useResetIslandQueue,
} from './context';
import DesktopProgressView from './DesktopProgressView';
import { isIslandMockEnabled } from './mockIsland';

// 전역 1개만 마운트. 새 job(null→값)이면 모달 자동 확장, 새로고침 복원 시엔 위젯(축소) 유지
const DesktopProgress = () => {
  const jobId = useBatchReplyV3JobIdValue();
  const { clear: clearJobId } = useBatchReplyV3JobId();
  const { clear: clearDim } = useOptimisticDim();
  const resetQueue = useResetIslandQueue();
  const { data: status } = useBatchReplyV3Status(jobId ?? null);
  const { mutate: confirmBatch } = useConfirmBatchReplyV3();

  const [expanded, setExpanded] = useBatchReplyV3Expanded();
  const prevJobId = useRef<string | null | undefined>(undefined);
  const isMobile = useMobile();

  useEffect(() => {
    if (prevJobId.current !== undefined && !prevJobId.current && jobId) {
      setExpanded(true);
    }
    prevJobId.current = jobId;
  }, [jobId]);

  // <768(모바일)에선 렌더 자체를 막음 — v4 Modal이 portal(document.body)이라
  // layout의 `hidden md:block` CSS만으론 모달이 안 가려짐(포털이 wrapper 밖으로 빠짐).
  if (isMobile) return null;

  if (!jobId || !status) return null;

  const clearLocal = () => {
    clearJobId();
    resetQueue();
    clearDim();
  };

  // 닫기 = dismiss. 세션 재채택 방지(dead-set) + 배너 즉시 제거 + best-effort 서버 confirm.
  // 실패(terminal-non-COMPLETED) job은 BE confirm이 실패할 수 있으나 로컬 dismiss로 확실히 닫힌다.
  const handleConfirm = () => {
    dismissBatchJob(jobId);
    clearLocal();
    if (!isIslandMockEnabled()) confirmBatch(jobId);
  };

  // 성공 종결(COMPLETED/CONFIRMED) = 완료, 실패 종결(FAILED/CANCELLED/EXPIRED) = 오류.
  // ⚠️ CONFIRMED를 "terminal && !COMPLETED"로 실패 취급하면 성공 job이 오류 카드로 뜬다(회귀 방지).
  const isCompleted = isSuccessTerminalV3Status(status.status);
  const isFailed = isFailedTerminalV3Status(status.status);

  return (
    <DesktopProgressView
      data={{
        total: status.stats?.total ?? 0,
        pending: status.stats?.pending ?? 0,
        completed: status.stats?.completed ?? 0,
        failed: status.stats?.failed ?? 0,
        isCompleted,
        isFailed,
      }}
      expanded={expanded}
      onExpand={() => setExpanded(true)}
      onCollapse={() => setExpanded(false)}
      onConfirm={handleConfirm}
    />
  );
};

export default DesktopProgress;
